import {
    CustomerManagementFilterModel,
    RestaurantFilterModel,
    RestaurantTypesManagementModel,
    RestaurantPaymentDetailsModel,
    TaxRequestModel,
    CreateRestaurantGroupModel,
    PagedList,
    RestaurantsGroupModel,
    RestaurantsGroupsListFilter,
    RestaurantsGroupDetailsModel,
    EditRestaurantGroupModel,
    DishStock,
    RestaurantsStock,
    ExportOwnerDishModel,
    RestaurantGroup,
    RestaurantFinance,
    RestaurantPaymentReportDTO,
    GroupSales,
    PricingLevelAccessCode,
    IdName,
} from 'api/models';
import {
    RestaurantDetails,
    QRData,
    QRCodesData,
    RestaurantAdvancedSettings,
    AutomaticZClosingSettings,
    ReservationModuleUpdateModel,
} from 'store/restaurantDetailsStore/reducer';
import { RestaurantCreateModel, RestaurantPaymentSettings } from 'store/restaurantsStore/reducer';
import agent, { prepareHeaders } from 'api/agent';
import { RestaurantCreateData } from 'components/restaurants/CreateEditRestaurantPage';
import axios, { AxiosResponse } from 'axios';
import { VoucherStatus } from 'components/restaurantInformation/vouchers/components/VoucherStatusBox';
import { parseFileNameFromContentDisposition } from 'helpers/helperFunctions';
import { ServiceValue } from 'components/restaurantInformation/components/types';

export interface RestaurantCopyModel {
    restaurantModel: RestaurantCreateData;
    workingHours: boolean;
    workAreas: boolean;
    qrCodes: boolean;
    priceLevels: boolean;
    menus: boolean;
    users: boolean;
}

export interface Voucher {
    id: string;
    voucherId: string;
    value: string;
    expirationDate: number;
    useDate: number;
    uploadDate: number;
    status: VoucherStatus;
}

function downloadAccessCodePdf(url: string, name: string) {
    return axios
        .get(url, {
            ...prepareHeaders(),
            responseType: 'blob',
        })
        .then(({ data, headers }) => {
            const blob = data;
            const filename = parseFileNameFromContentDisposition(
                headers['content-disposition'],
                name,
                'pdf'
            );
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);
        });
}

export const Restaurants = {
    Create: (model: RestaurantCreateModel) =>
        agent.requests.put('/Restaurant/create', model, prepareHeaders()),
    Update: (id: string, model: RestaurantCreateModel) =>
        agent.requests.post(`Restaurant/${id}`, model),
    GetList: (
        filterModel: RestaurantFilterModel | null = null,
        page?: number,
        limit?: number,
        asc?: boolean,
        orderBy?: string
    ) =>
        agent.requests.get(
            '/Restaurant/list',
            filterModel != null
                ? {
                      params: {
                          Name: filterModel!.name,
                          AddressLine: filterModel!.addressLine,
                          ZIP: filterModel!.zip,
                          City: filterModel!.city,
                          ShowCoolBoxes: filterModel.showCoolBoxes,
                          page: page,
                          limit: limit,
                          asc: asc,
                          orderBy: orderBy,
                      },
                  }
                : {
                      params: {
                          page: page,
                          limit: limit,
                          asc: asc,
                          orderBy: orderBy,
                      },
                  }
        ),
    GetDetails: (id: string) => agent.requests.get(`Restaurant/${id}`),
        UpdateReservationModule: (model: ReservationModuleUpdateModel) =>
            agent.requests.post('Restaurant/reservation-module', model),
        GetRestaurantCustomers: (
        filterModel: CustomerManagementFilterModel | null = null,
        page?: number,
        limit?: number,
        asc?: boolean,
        orderBy?: string
    ) => {
        const params = new URLSearchParams();
        if (filterModel?.name) {
            params.append('name', filterModel.name);
        }
        if (filterModel?.email) {
            params.append('email', filterModel.email);
        }
        if (filterModel?.phoneNumber) {
            params.append('phoneNumber', filterModel.phoneNumber);
        }
        if (filterModel?.priceLevel) {
            params.append('priceLevel', filterModel.priceLevel);
        }
        if (filterModel?.numberOfOrders) {
            params.append('numberOfOrders', filterModel.numberOfOrders.toString());
        }
        if ((filterModel?.isRegistered?.length ?? 0) === 1) {
            params.append('isRegistered', filterModel!.isRegistered[0].toString());
        }
        if (page !== undefined) {
            params.append('page', page.toString());
        }
        if (limit !== undefined) {
            params.append('limit', limit.toString());
        }
        if (asc !== undefined) {
            params.append('asc', asc.toString());
        }
        if (orderBy) {
            params.append('orderBy', orderBy);
        }

        return agent.requests.get('Restaurant/customers', { params });
    },
    GetRestaurantInformation: () => agent.requests.get('Restaurant/getdetails'),
    UpdateRestaurantInformation: (model: RestaurantDetails) =>
        agent.requests.post('Restaurant/updatedetails', model),
    GetCuisineTypes: () => agent.requests.get('Restaurant/types'),
    CreateCuisineType: (model: { name: string }) =>
        agent.requests.put('Restaurant/types', model, prepareHeaders()),
    ManageCuisineTypes: (model: RestaurantTypesManagementModel) =>
        agent.requests.post('Restaurant/types', model),
    GetQrs: (params = {}) => agent.requests.get('Restaurant/qrcodes', { params }),
    CreateQrCode: (qrCode: QRData, params = {}) =>
        agent.requests.put('Restaurant/qrcodes', qrCode, { ...prepareHeaders(), params }),
    UpdateQrCode: (qrCode: QRData) => agent.requests.post('Restaurant/qrcodes', qrCode),
    DeleteQrCode: (qrIds: string[]) => agent.requests.delete('Restaurant/qrcodes', qrIds),
    GetFinance: (dateFrom: any, dateTo: any, restaurantId = ''): Promise<RestaurantFinance> =>
        agent.requests.get('Restaurant/finance', {
            params: {
                dateFrom,
                dateTo,
                restaurantId,
            },
        }),
    GetFinanceExcel: (dateFrom: string, dateTo: string, restaurantId?: string) =>
        axios.get('Restaurant/finance/export-excel', {
            ...prepareHeaders(true),
            params: {
                dateFrom,
                dateTo,
                restaurantId,
            },
            responseType: 'blob',
        }),
    GetTipsData: (dateFrom: any, dateTo: any, restaurantId = '') =>
        agent.requests.get('Restaurant/finance/tips', {
            params: {
                dateFrom,
                dateTo,
                restaurantId,
            },
        }),
    GetStornoData: (dateFrom: any, dateTo: any, restaurantId = '') =>
        agent.requests.get('Restaurant/finance/storno', {
            params: {
                dateFrom,
                dateTo,
                restaurantId,
            },
        }),
    DisableRestaurant: (id: string) => agent.requests.post(`Restaurant/disable/${id}`),
    UpdatePaymentDetails: (model: RestaurantPaymentDetailsModel) =>
        agent.requests.post('Restaurant/paymentdetails', model),
    UpdateQrCodesNames: (model: QRCodesData) =>
        agent.requests.post('Restaurant/qrcodes/name', model),
    GetTaxes: () => agent.requests.get('Restaurant/taxes'),
    UpdateTaxes: (id: string, model: TaxRequestModel) =>
        agent.requests.post(`Restaurant/taxes/${id}`, model),
    CreatePricingLevel: (restaurantId: string, name: string) =>
        agent.requests.post(`Restaurant/${restaurantId}/pricinglevel`, { name }),
    UpdatePricingLevel: (restaurantId: string, id: string, name: string) =>
        agent.requests.put(
            `Restaurant/${restaurantId}/pricinglevel/${id}`,
            { name },
            prepareHeaders()
        ),
    DeletePricingLevel: (restaurantId: string, id: string) =>
        agent.requests.delete(`Restaurant/${restaurantId}/pricinglevel/${id}`),
    CreatePricingLevelAccessCode: (
        restaurantId: string,
        priceLevelId: string,
        baseUrl?: string
    ): Promise<PricingLevelAccessCode> =>
        agent.requests.post(`Restaurant/${restaurantId}/pricinglevel/${priceLevelId}/accesscode`, {
            baseUrl,
        }),
    GeneratePricingLevelAccessCodePDF: (restaurantId: string, priceLevelId: string, name: string) =>
        downloadAccessCodePdf(
            `Restaurant/${restaurantId}/pricinglevel/${priceLevelId}/accesscode/pdf`,
            name
        ),
    CreateGroupPricingLevel: (
        restaurantId: string,
        groupId: string,
        name: string
    ): Promise<IdName> =>
        agent.requests.post(`Restaurant/${restaurantId}/groups/${groupId}/pricinglevel`, { name }),
    UpdateGroupPricingLevel: (
        restaurantId: string,
        groupId: string,
        id: string,
        name: string
    ): Promise<IdName> =>
        agent.requests.put(
            `Restaurant/${restaurantId}/groups/${groupId}/pricinglevel/${id}`,
            { name },
            prepareHeaders()
        ),
    DeleteGroupPricingLevel: (restaurantId: string, groupId: string, id: string): Promise<string> =>
        agent.requests.delete(`Restaurant/${restaurantId}/groups/${groupId}/pricinglevel/${id}`),
    CreateGroupPricingLevelAccessCode: (
        restaurantId: string,
        groupId: string,
        priceLevelId: string,
        baseUrl?: string
    ): Promise<PricingLevelAccessCode> =>
        agent.requests.post(
            `Restaurant/${restaurantId}/groups/${groupId}/pricinglevel/${priceLevelId}/accesscode`,
            { baseUrl }
        ),
    GenerateGroupPricingLevelAccessCodePDF: (
        restaurantId: string,
        groupId: string,
        priceLevelId: string,
        name: string
    ) =>
        downloadAccessCodePdf(
            `Restaurant/${restaurantId}/groups/${groupId}/pricinglevel/${priceLevelId}/accesscode/pdf`,
            name
        ),
    GetSimpleList: () => agent.requests.get('/Restaurant/simplelist'),
    CopyRestaurant: (restaurantId: string, model: RestaurantCopyModel) =>
        agent.requests.post(`Restaurant/${restaurantId}/copy`, model),
    GetRestaurantsGroups: (
        params: RestaurantsGroupsListFilter
    ): Promise<PagedList<RestaurantsGroupModel>> => {
        const searchParams: { [key: string]: any } = {};
        if (params.name) searchParams.name = params.name;
        if (params.address) searchParams.address = params.address;
        if (params.zip) searchParams.zip = params.zip;
        if (params.city) searchParams.city = params.city;
        searchParams.showCoolBoxes = !!params.showCoolBoxes;
        searchParams.page = params.page !== undefined ? params.page : 0;
        searchParams.limit = params.limit || 10;
        return agent.requests.get('Restaurant/group', {
            params: searchParams,
        });
    },
    GetRestaurantGroup: (id: string): Promise<RestaurantsGroupDetailsModel> =>
        agent.requests.get(`Restaurant/group/${id}`),
    CreateRestaurantGroup: (model: CreateRestaurantGroupModel): Promise<RestaurantsGroupModel> =>
        agent.requests.post('Restaurant/group', model),
    UpdateRestaurantGroup: (
        id: string,
        model: EditRestaurantGroupModel
    ): Promise<RestaurantsGroupModel> =>
        agent.requests.put(`Restaurant/group/${id}`, model, prepareHeaders()),
    DisableRestaurantGroup: (id: string): Promise<RestaurantsGroupModel> =>
        agent.requests.post(`Restaurant/group/${id}/enabledisable`),
    DeleteRestaurantGroup: (id: string): Promise<string> =>
        agent.requests.delete(`Restaurant/group/${id}`),
    SetPaymentSettings: (id: string, model: RestaurantPaymentSettings) =>
        agent.requests.post(`Restaurant/${id}/payment-settings`, model),
    UpdateRestaurantAdvancedSettings: (model: RestaurantAdvancedSettings) =>
        agent.requests.post('Restaurant/advanced-settings', model),
    UpdateAutomaticZClosingSettings: (
        model: AutomaticZClosingSettings
    ): Promise<AutomaticZClosingSettings> =>
        agent.requests.post('Restaurant/automatic-z-closing-settings', model),
    GetStornoReasons: () => agent.requests.get('Restaurant/storno-reasons'),
    GetStock: (from: string, to: string): Promise<DishStock[]> =>
        agent.requests.get('Restaurant/stock', { params: { from, to } }),
    GetOwnerRestaurantsStock: (from: string, to: string): Promise<RestaurantsStock> =>
        agent.requests.get('Restaurant/stock/owner', { params: { from, to } }),
    GetVouchers: (
        page: number = 0,
        limit: number = 0,
        status: string | null = null,
        dateFrom: number = 0,
        dateTo: number = 0
    ): Promise<PagedList<Voucher>> =>
        agent.requests.get('Restaurant/vouchers', {
            params: {
                page,
                limit,
                status,
                dateFrom,
                dateTo,
            },
        }),
    ImportVouchers: (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append('file', file);
        return agent.requests.post('Restaurant/vouchers/import', formData);
    },
    GetVouchersTemplate: () =>
        axios.get('Restaurant/vouchers/template', { ...prepareHeaders(), responseType: 'blob' }),
    ExportStock: (from: string, to: string, dishIds: string[]): Promise<AxiosResponse<Blob>> => {
        return axios.post('Restaurant/stock-export', dishIds, {
            ...prepareHeaders(),
            params: { from, to },
            responseType: 'blob',
        });
    },
    ExportOwnerStock: (
        from: string,
        to: string,
        groupName: string,
        dishes: ExportOwnerDishModel[]
    ): Promise<AxiosResponse<Blob>> => {
        return axios.post(
            'Restaurant/owner/stock-export',
            {
                groupName,
                dishes,
            },
            {
                ...prepareHeaders(),
                params: { from, to },
                responseType: 'blob',
            }
        );
    },
    GetMyGroups: (): Promise<RestaurantGroup[]> => {
        return agent.requests.get('Restaurant/my-groups');
    },
    GetPaymentsReport: (
        from: string,
        to: string,
        restaurantId?: string
    ): Promise<RestaurantPaymentReportDTO> => {
        var params = new URLSearchParams({
            dateFrom: from,
            dateTo: to,
        });

        if (restaurantId) {
            params.append('restaurantId', restaurantId);
        }
        return agent.requests.get('Restaurant/finance-payments', { params });
    },
    GetPaymentsReportExcel: (
        from: string,
        to: string,
        restaurantId?: string
    ): Promise<AxiosResponse<Blob>> => {
        var params = new URLSearchParams({
            dateFrom: from,
            dateTo: to,
        });

        if (restaurantId) {
            params.append('restaurantId', restaurantId);
        }
        return axios.get('Restaurant/finance-payments/export-excel', {
            ...prepareHeaders(),
            params,
            responseType: 'blob',
        });
    },
    GetCurrentRestaurantManagedGroups: (): Promise<RestaurantGroup[]> => {
        return agent.requests.get('Restaurant/current-restaurant-managed-groups');
    },
    GetGroupSalesReport: (groupId: string, from: string, to: string): Promise<GroupSales> => {
        var params = new URLSearchParams({
            from,
            to,
        });

        return agent.requests.get(`Restaurant/group/${groupId}/sales`, { params });
    },
    GetGroupSalesExcel: (
        groupId: string,
        from: string,
        to: string
    ): Promise<AxiosResponse<Blob>> => {
        var params = new URLSearchParams({
            from,
            to,
        });

        return axios.get(`Restaurant/group/${groupId}/sales-export-excel`, {
            ...prepareHeaders(),
            params,
            responseType: 'blob',
        });
    },
    GetGroupPaymentReport: (
        groupId: string,
        from: string,
        to: string
    ): Promise<RestaurantPaymentReportDTO> => {
        var params = new URLSearchParams({
            from,
            to,
        });

        return agent.requests.get(`Restaurant/group/${groupId}/payments`, { params });
    },
    getGroupPaymentReportExcel: (
        groupId: string,
        from: string,
        to: string
    ): Promise<AxiosResponse<Blob>> => {
        var params = new URLSearchParams({
            from,
            to,
        });

        return axios.get(`Restaurant/group/${groupId}/payments-export-excel`, {
            ...prepareHeaders(),
            params,
            responseType: 'blob',
        });
    },
    UpdateService: (service: string, model: ServiceValue): Promise<RestaurantDetails> => {
        return agent.requests.put(`Restaurant/service/${service}`, model, prepareHeaders());
    },
};
